import { apiFetch, isApiMode } from './http';

// The payment-exception worklist for /sa → «Betalingsavvik» (#1131).
// `GET /platform/payment-exceptions` takes no parameters, decides who may read
// (`platform.payment_exceptions.read`, Entra only) and records every read.
// The shapes mirror `apps/api/src/payments/exceptionWorklist.ts`.

export interface ExceptionPayment {
  paymentId: string;
  organization: string;
  state: string;
  exceptionReason: string;
  /** Integer øre (or the currency's minor unit), exactly as the row holds it. */
  amountMinor: number;
  currency: string;
  bookingId: string | null;
  bookingExists: boolean;
  bookingPaymentStatus: string | null;
  providerTransactionId: string | null;
  updatedAt: string;
  /** The runbook's triage, verbatim — it begins with its level, `0`…`3`. */
  triage: string;
}

export interface UnmatchedFact {
  provider: string | null;
  providerEventId: string;
  process: string | null;
  deliveries: number;
  firstSeen: string;
  lastSeen: string;
}

export interface ExceptionWorklist {
  payments: ExceptionPayment[];
  paymentsTruncated: boolean;
  unmatched: UnmatchedFact[];
  unmatchedTruncated: boolean;
}

export type WorklistResult =
  | { state: 'no-api' }
  | { state: 'ok'; worklist: ExceptionWorklist }
  | { state: 'refused'; status: number; code: string | null }
  | { state: 'error'; status: number | null };

/** The triage level a row carries: its first character, `0`…`3`, or null for anything else. */
export function triageLevel(triage: string): 0 | 1 | 2 | 3 | null {
  // A DIGIT, read as one — not `Number(triage[0])`: `Number('')` is 0, which
  // would show a row with no triage as «resolved», the least urgent level.
  const m = /^([0-3])/.exec(triage);
  return m ? (Number(m[1]) as 0 | 1 | 2 | 3) : null;
}

/** Both lists. Every call is recorded by the server as a read. */
export async function fetchPaymentExceptions(): Promise<WorklistResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/platform/payment-exceptions');
    if (res.ok) return { state: 'ok', worklist: (await res.json()) as ExceptionWorklist };
    if (res.status >= 500) return { state: 'error', status: res.status };
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    return {
      state: 'refused',
      status: res.status,
      code: typeof body?.error === 'string' ? body.error : null,
    };
  } catch {
    return { state: 'error', status: null };
  }
}
